/**
 * Drizzle mutations (PowerSync local-first) for the FieldCheck domain.
 *
 * Every write goes to local SQLite; PowerSync uploads it in the background and
 * the server (`SyncRepository`) re-validates permissions. `user_id` + `organization_id`
 * are denormalized on every row (required by the sync rules).
 */

import { eq } from "drizzle-orm";
import {
  attachment,
  finding,
  inspection,
  inspection_answer,
  type PowerSyncDrizzleDB,
  site,
  template,
  template_item,
} from "@fieldcheck/powersync";
import { generateUUID } from "@fleet/utils";
import { nowISO } from "../utils/date-helpers";
import {
  patchRowVerified,
  type VerifiedWriteOptions,
  waitForRowPresent,
} from "../powersync/verified-write";
import {
  type AnswerResult,
  FINDING_TERMINAL_STATUS,
  type FindingSeverity,
  type FindingStatus,
  type TemplateItemType,
} from "./types";

/**
 * Every `UPDATE … WHERE id = ?` in this file goes through
 * `patchRowVerified` / `waitForRowPresent`: a raw UPDATE against the PowerSync
 * view can match ZERO rows and return "success" while the newly created row is
 * still traveling to the server (see `../powersync/verified-write.ts`). In
 * FieldCheck this protects inspection parts, findings, and checklists that the
 * field operator believes were saved — compliance evidence. Mutations
 * **may throw** (`LocalWriteLostError`): the caller must report it, never
 * swallow it.
 */

function toFlag(value: boolean | undefined, fallback = 0): number {
  if (value === undefined) {
    return fallback;
  }
  return value ? 1 : 0;
}

// ─── Sites ───────────────────────────────────────────────────────────────────

export async function createSiteMutation(
  db: PowerSyncDrizzleDB,
  input: {
    userId: string;
    organizationId?: string | null;
    name: string;
    address?: string | null;
    notes?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(site).values({
    id,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    name: input.name,
    address: input.address ?? null,
    notes: input.notes ?? null,
    archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateSiteMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    address: string | null;
    notes: string | null;
    archived: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, site, site.id, id, updates, options);
}

export async function deleteSiteMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(site).where(eq(site.id, id));
}

// ─── Templates ──────────────────────────────────────────────────────────────

export async function createTemplateMutation(
  db: PowerSyncDrizzleDB,
  input: {
    userId: string;
    organizationId?: string | null;
    name: string;
    description?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(template).values({
    id,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    name: input.name,
    description: input.description ?? null,
    archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateTemplateMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    description: string | null;
    archived: number;
    organization_id: string | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, template, template.id, id, updates, options);
}

/**
 * SOFT-DELETE (trash): in field-check, traced entities are NEVER hard-deleted —
 * the evidence and its trail are the product (compliance). Items are preserved
 * (hidden with the template; they reappear when restored).
 */
export async function deleteTemplateMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, template, template.id, id, { deleted_at: nowISO() }, options);
}

/** Restores a template from the trash. */
export async function restoreTemplateMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, template, template.id, id, { deleted_at: null }, options);
}

export async function createTemplateItemMutation(
  db: PowerSyncDrizzleDB,
  input: {
    templateId: string;
    userId: string;
    organizationId?: string | null;
    label: string;
    itemType: TemplateItemType;
    section?: string | null;
    required?: boolean;
    photoRequired?: boolean;
    minValue?: number | null;
    maxValue?: number | null;
    unit?: string | null;
    sortOrder?: number;
    conditionItemId?: string | null;
    conditionValue?: string | null;
    score?: number | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(template_item).values({
    id,
    template_id: input.templateId,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    section: input.section ?? null,
    label: input.label,
    item_type: input.itemType,
    required: toFlag(input.required, 1),
    photo_required: toFlag(input.photoRequired, 0),
    min_value: input.minValue ?? null,
    max_value: input.maxValue ?? null,
    unit: input.unit ?? null,
    sort_order: input.sortOrder ?? 0,
    condition_item_id: input.conditionItemId ?? null,
    condition_value: input.conditionValue ?? null,
    score: input.score ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateTemplateItemMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    section: string | null;
    label: string;
    item_type: string;
    required: number;
    photo_required: number;
    min_value: number | null;
    max_value: number | null;
    unit: string | null;
    sort_order: number;
    condition_item_id: string | null;
    condition_value: string | null;
    score: number | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, template_item, template_item.id, id, updates, options);
}

export async function deleteTemplateItemMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(template_item).where(eq(template_item.id, id));
}

// ─── TEMPLATE VERSIONING: restore a version (forward write) ──────────────────

/** An item exactly as serialized in `template_version.items`. */
interface VersionItemNode {
  section: string | null;
  label: string;
  item_type: string;
  required: boolean;
  photo_required: boolean;
  min_value: number | null;
  max_value: number | null;
  unit: string | null;
  sort_order: number;
  condition_item_id: string | null;
  condition_value: string | null;
  score: number | null;
}

/** Minimum snapshot needed to restore (from the local bucket or the API). */
export interface TemplateVersionSnapshot {
  name: string | null;
  description: string | null;
  /** `items` may arrive as a JSON string (local bucket) or an already-parsed array (API). */
  items: string | unknown[] | null;
}

function asNumberOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}
function asStringOrNull(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/** Parses `template_version.items` (tolerant) into normalized nodes. */
export function parseVersionItems(items: string | unknown[] | null): VersionItemNode[] {
  let raw: unknown = items;
  if (typeof items === "string") {
    try {
      raw = JSON.parse(items);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((x): x is Record<string, unknown> => Boolean(x) && typeof x === "object")
    .map((node, index) => ({
      section: asStringOrNull(node.section),
      label: asStringOrNull(node.label) ?? "",
      item_type: asStringOrNull(node.item_type) ?? "yes_no",
      required: node.required !== false,
      photo_required: node.photo_required === true,
      min_value: asNumberOrNull(node.min_value),
      max_value: asNumberOrNull(node.max_value),
      unit: asStringOrNull(node.unit),
      sort_order: asNumberOrNull(node.sort_order) ?? index,
      condition_item_id: asStringOrNull(node.condition_item_id),
      condition_value: asStringOrNull(node.condition_value),
      score: asNumberOrNull(node.score),
    }));
}

/**
 * Restores a version from history (TEMPLATE VERSIONING): NORMAL forward write
 * in ONE transaction (name/description + REPLACEMENT of the item set to match
 * the snapshot). It never rewrites history: the server write path materializes
 * the restored content as a NEW (current) version, and seals on existing
 * inspections do NOT change. It does NOT touch archived/deleted_at.
 *
 * It must run in `db.transaction` so PowerSync uploads the gesture as ONE batch
 * (one new version). Items are recreated with new ids (the snapshot does not
 * store ids); `condition_item_id` is preserved verbatim (it may dangle if it
 * pointed to a recreated item — a known limitation of snapshot restore).
 *
 * `waitForRowPresent` is called BEFORE opening the transaction (never inside:
 * retrying with the write lock held would stall the entire transaction).
 * If `templateId` were not yet in the view, the inner `UPDATE` would match
 * nothing but the item `DELETE`+`INSERT` would still happen: the template would
 * have the restored version's items and the old
 * `name`/`description` — an inconsistent mix, not just a lost field.
 */
export async function restoreTemplateVersionMutation(
  db: PowerSyncDrizzleDB,
  templateId: string,
  version: TemplateVersionSnapshot,
  meta: { userId: string; organizationId?: string | null },
  options?: VerifiedWriteOptions,
): Promise<void> {
  await waitForRowPresent(db, template, template.id, templateId, options);
  const nodes = parseVersionItems(version.items).toSorted((a, b) => a.sort_order - b.sort_order);
  await db.transaction(async (trx) => {
    await trx
      .update(template)
      .set({
        name: version.name ?? "",
        description: version.description ?? null,
        updated_at: nowISO(),
      })
      .where(eq(template.id, templateId));
    // Replaces the item set to match the snapshot exactly.
    await trx.delete(template_item).where(eq(template_item.template_id, templateId));
    const now = nowISO();
    for (const node of nodes) {
      await trx.insert(template_item).values({
        id: generateUUID(),
        template_id: templateId,
        user_id: meta.userId,
        organization_id: meta.organizationId ?? null,
        section: node.section,
        label: node.label,
        item_type: node.item_type,
        required: node.required ? 1 : 0,
        photo_required: node.photo_required ? 1 : 0,
        min_value: node.min_value,
        max_value: node.max_value,
        unit: node.unit,
        sort_order: node.sort_order,
        condition_item_id: node.condition_item_id,
        condition_value: node.condition_value,
        score: node.score,
        created_at: now,
        updated_at: now,
      });
    }
  });
}

// ─── Inspections ────────────────────────────────────────────────────────────

export async function createInspectionMutation(
  db: PowerSyncDrizzleDB,
  input: {
    templateId: string;
    siteId?: string | null;
    userId: string;
    organizationId?: string | null;
    inspectorId?: string | null;
    notes?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(inspection).values({
    id,
    template_id: input.templateId,
    site_id: input.siteId ?? null,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    inspector_id: input.inspectorId ?? input.userId,
    status: "draft",
    notes: input.notes ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateInspectionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    site_id: string | null;
    inspector_id: string | null;
    status: string;
    notes: string | null;
    started_at: string | null;
    completed_at: string | null;
    signed_at: string | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, inspection, inspection.id, id, updates, options);
}

/** draft → in progress (sets started_at the first time). */
export async function startInspectionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  alreadyStartedAt?: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateInspectionMutation(
    db,
    id,
    {
      status: "in_progress",
      started_at: alreadyStartedAt ?? nowISO(),
    },
    options,
  );
}

/** in progress → completed. `signedAt` arrives when signed on the canvas. */
export async function completeInspectionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  opts?: { signedAt?: string | null },
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateInspectionMutation(
    db,
    id,
    {
      status: "completed",
      completed_at: nowISO(),
      signed_at: opts?.signedAt ?? null,
    },
    options,
  );
}

/**
 * SOFT-DELETE (trash): the inspection is EVIDENCE — "delete permanently" does
 * not exist in field-check. Responses/photos are preserved intact
 * (hidden with the inspection; they reappear when restored).
 */
export async function deleteInspectionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, inspection, inspection.id, id, { deleted_at: nowISO() }, options);
}

/** Restores an inspection from the trash. */
export async function restoreInspectionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, inspection, inspection.id, id, { deleted_at: null }, options);
}

// ─── Responses ──────────────────────────────────────────────────────────────

export interface AnswerValues {
  value_bool?: number | null;
  value_number?: number | null;
  value_text?: string | null;
  is_na?: number;
  note?: string | null;
  result?: AnswerResult | null;
}

/**
 * Creates or updates an item's response. `existingId` saves the lookup
 * when the UI already knows the row (in-memory response map).
 */
export async function upsertAnswerMutation(
  db: PowerSyncDrizzleDB,
  input: {
    existingId?: string | null;
    inspectionId: string;
    templateItemId: string;
    userId: string;
    organizationId?: string | null;
    values: AnswerValues;
  },
  options?: VerifiedWriteOptions,
): Promise<string> {
  const now = nowISO();
  if (input.existingId) {
    await patchRowVerified(
      db,
      inspection_answer,
      inspection_answer.id,
      input.existingId,
      { ...input.values },
      options,
    );
    return input.existingId;
  }
  const id = generateUUID();
  await db.insert(inspection_answer).values({
    id,
    inspection_id: input.inspectionId,
    template_item_id: input.templateItemId,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    value_bool: input.values.value_bool ?? null,
    value_number: input.values.value_number ?? null,
    value_text: input.values.value_text ?? null,
    is_na: input.values.is_na ?? 0,
    note: input.values.note ?? null,
    result: input.values.result ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ─── Findings ───────────────────────────────────────────────────────────────

export async function createFindingMutation(
  db: PowerSyncDrizzleDB,
  input: {
    userId: string;
    organizationId?: string | null;
    inspectionId?: string | null;
    inspectionAnswerId?: string | null;
    siteId?: string | null;
    title: string;
    description?: string | null;
    severity: FindingSeverity;
    assigneeId?: string | null;
    dueDate?: string | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(finding).values({
    id,
    inspection_id: input.inspectionId ?? null,
    inspection_answer_id: input.inspectionAnswerId ?? null,
    site_id: input.siteId ?? null,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    title: input.title,
    description: input.description ?? null,
    severity: input.severity,
    status: "open",
    assignee_id: input.assigneeId ?? null,
    due_date: input.dueDate ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateFindingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    title: string;
    description: string | null;
    severity: string;
    status: string;
    assignee_id: string | null;
    due_date: string | null;
    closed_at: string | null;
    site_id: string | null;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, finding, finding.id, id, updates, options);
}

/**
 * Changes a finding's state along the pipeline
 * (open → in_progress → resolved). Sets/clears `closed_at` as appropriate:
 * resolving sets the close date; any other state clears it (reopen).
 */
export async function setFindingStatusMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  status: FindingStatus,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateFindingMutation(
    db,
    id,
    {
      status,
      closed_at: status === FINDING_TERMINAL_STATUS ? nowISO() : null,
    },
    options,
  );
}

/** Marks a finding as resolved (terminal state) and sets `closed_at`. */
export async function resolveFindingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await setFindingStatusMutation(db, id, FINDING_TERMINAL_STATUS, options);
}

/** Reopens a resolved finding: returns to 'open' and clears `closed_at`. */
export async function reopenFindingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await setFindingStatusMutation(db, id, "open", options);
}

/**
 * @deprecated Use `resolveFindingMutation`. Kept for compatibility:
 * "closing" a finding means resolving it.
 */
export async function closeFindingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await resolveFindingMutation(db, id, options);
}

/** SOFT-DELETE (trash): findings are never hard-deleted either. */
export async function deleteFindingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, finding, finding.id, id, { deleted_at: nowISO() }, options);
}

/** Restores a finding from the trash. */
export async function restoreFindingMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, finding, finding.id, id, { deleted_at: null }, options);
}

// ─── Attachments (item/finding photos, signature) ───────────────────────────

const BYTES_PER_KB = 1024;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;
const MAX_ATTACHMENT_SIZE_MB = 25;

/**
 * Maximum allowed size for an attachment (item/finding photo, signature).
 *
 * It is validated on the client before upload to provide immediate feedback and
 * avoid large requests the backend would reject anyway.
 */
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * BYTES_PER_MB;

export async function addFieldworkAttachmentMutation(
  db: PowerSyncDrizzleDB,
  input: {
    userId: string;
    organizationId?: string | null;
    /** Exactly ONE of the three parents. */
    inspectionAnswerId?: string | null;
    findingId?: string | null;
    inspectionId?: string | null;
    filename: string;
    url: string;
    contentType: string;
    size: number;
    /** Optional GEO seal (proof-of-presence) captured when uploading the photo. */
    latitude?: number | null;
    longitude?: number | null;
    geoAccuracy?: number | null;
  },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(attachment).values({
    id,
    inspection_answer_id: input.inspectionAnswerId ?? null,
    finding_id: input.findingId ?? null,
    inspection_id: input.inspectionId ?? null,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    filename: input.filename,
    url: input.url,
    content_type: input.contentType,
    size: input.size,
    latitude: input.latitude ?? null,
    longitude: input.longitude ?? null,
    geo_accuracy: input.geoAccuracy ?? null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteFieldworkAttachmentMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(attachment).where(eq(attachment.id, id));
}
